import { Button, InputGroup } from "@clarkinc/zest-design-system/ui";
import React, { useRef, useState } from "react";
import { sendCustomerIoNewsletterSignupEvent } from "~/analytics/CustomerIo/EmailEvents";
import { validateEmail } from "~/common/Email/EmailValidator";

interface Props {
  showOptoutMessage: boolean;
}

const OutletWelcome = ({ showOptoutMessage }: Props) => {
  const [emailAddress, setEmailAddress] = useState("");
  const [emailResponse, setEmailResponse] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);

  const submitOutletEmailNotification = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();

    const isValidEmail = validateEmail(emailAddress);

    if (isValidEmail) {
      const formData = new URLSearchParams();
      formData.append("txtEmailAddress", emailAddress);
      formData.append("btnOutletSubmit", "Sign Me Up!");

      await fetch("/api/customers/notifications/outlet/add", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData.toString(),
      })
        .then(async () => {
          setEmailResponse("Success");

          const { emailSignupEvent } = await import("~/gtm");
          emailSignupEvent(emailAddress);
          sendCustomerIoNewsletterSignupEvent(emailAddress);
        })
        .catch(() => {
          setEmailResponse("Error");
          inputRef.current?.focus();
        });
    } else {
      setEmailResponse("Validation");
      inputRef.current?.focus();
    }
  };

  const getErrorMessage = (response: string) => {
    switch (response) {
      case "Validation":
        return "Please enter a valid email address";
      case "Error":
        return "Unable to complete request, please try again";
      default:
        return "";
    }
  };

  const displayEmailOptions = () => {
    if (showOptoutMessage) {
      return (
        <p className="mb-0 font-bold text-xl leading-tight text-center">
          You&apos;ve been unsubscribed
          <br />
          from Outlet emails!
        </p>
      );
    }

    if (emailResponse === "Success") {
      return (
        <p className="mb-0 font-bold text-xl leading-tight text-center">
          You&apos;ve been added to our
          <br />
          Outlet email list!
        </p>
      );
    }

    const hasError =
      emailResponse === "Validation" || emailResponse === "Error";

    return (
      <>
        <p
          className="mb-2 md:mb-4 font-bold text-xl leading-tight"
          id="default-input-label"
        >
          Sign up for updates on outlet products
        </p>
        <form
          className="form"
          onSubmit={(e) => submitOutletEmailNotification(e)}
          id="outletEmailForm"
        >
          <span className="sr-only">
            Enter Your Email Address to Sign up for updates on outlet products
          </span>
          <InputGroup
            groupLabel="Out Of Stock"
            hasError={hasError}
            errorMessages={getErrorMessage(emailResponse)}
            inputSize="medium"
            theme="wss"
          >
            <InputGroup.Wrapper>
              <InputGroup.Input
                theme="wss"
                type="email"
                name="txtEmailAddress"
                data-testid="email-address"
                aria-labelledby={
                  hasError ? "input-error" : "default-input-label"
                }
                maxLength={100}
                value={emailAddress}
                ref={inputRef}
                onValueChange={setEmailAddress}
                className="bg-gray-800 bg-opacity-50 shadow-none text-white"
              />
              <Button
                theme="wss"
                type="submit"
                name="btnOutletSubmit"
                value="Sign Up"
                size="medium"
                color="white"
                className="whitespace-nowrap"
              >
                Sign Up
              </Button>
            </InputGroup.Wrapper>
            <InputGroup.Error errorId="input-error" />
          </InputGroup>
        </form>
      </>
    );
  };

  return (
    <div
      className="lt:-mt-5"
      data-component="outlet-hero"
      style={{
        backgroundImage:
          "url('//cdnimg.webstaurantstore.com/uploads/design/2020/8/outlet-bg.jpg')",
      }}
    >
      <div className="container">
        <div className="flex flex-col sm:flex-row justify-between items-center py-3 sm:pt-8 md:py-8 lt:pt-[27px]">
          <img
            className="mt-6 w-4/5 sm:mb-4 sm:w-[341px] md:mt-0 md:mb-0 mr-4 md:w-1/2 lg:w-1/3 lg:pt-4 max-w-[480px]"
            src="https://www.webstaurantstore.com/uploads/design/2023/4/scratch-dent_long.svg"
            alt="Scatch and Dent Outlet"
          />
          <div
            data-component="outlet-signup"
            className="text-white p-5 rounded my-4 sm:my-0 w-4/5 sm:w-auto"
            style={{ backgroundColor: "rgb(0 0 0 / 29%)" }}
          >
            {displayEmailOptions()}
          </div>
        </div>
      </div>
    </div>
  );
};
export default OutletWelcome;
